import { Inviter } from 'sip.js';
import { cleanupCallGreeting, playInboundCallGreeting } from './callGreeting';
import { incomingRingFromSession } from './sipCallHelpers';
/**
 * SessionManager lifecycle hooks (incoming ring, outbound create, answered, hangup, register).
 */
export function createSessionManagerDelegate(ctx) {
    return {
        onServerConnect: () => {
            ctx.setSipStatus('connected');
            ctx.setSipError(null);
        },
        onServerDisconnect: (error) => {
            ctx.setSipStatus((prev) => (prev === 'registered' ? 'connected' : 'idle'));
            if (error?.message)
                ctx.setSipError(error.message);
        },
        onRegistered: () => {
            if (ctx.registrationStuckHintTimeoutRef.current !== undefined) {
                window.clearTimeout(ctx.registrationStuckHintTimeoutRef.current);
                ctx.registrationStuckHintTimeoutRef.current = undefined;
            }
            ctx.setSipStatus('registered');
            ctx.setSipError(null);
        },
        onUnregistered: () => {
            ctx.setSipStatus((s) => (s === 'registered' ? 'connected' : s));
        },
        onCallReceived: (session) => {
            if (ctx.isOnBreakRef.current || ctx.isOnAcwRef.current) {
                const sm = ctx.mgrRef.current;
                void sm?.decline(session).catch(() => undefined);
                return;
            }
            ctx.attachRingTerminatedListener(session);
            ctx.setIncomingRing({
                session,
                ...incomingRingFromSession(session),
            });
        },
        /** Outgoing INVITE and early session — warm-transfer consult must not steal primary refs. */
        onCallCreated: (session) => {
            if (session instanceof Inviter && ctx.warmConsultDialAllowedRef.current) {
                ctx.warmConsultDialAllowedRef.current = false;
                ctx.consultSessionRef.current = session;
                return;
            }
            ctx.conversationSessionRef.current = session;
            if (session instanceof Inviter) {
                ctx.subscribeConversationPhaseFnRef.current(session);
            }
        },
        onCallAnswered: (session) => {
            if (ctx.consultSessionRef.current === session) {
                ctx.setWarmTransfer((prev) => ({
                    ...prev,
                    phase: 'consult_established',
                }));
                ctx.setSipMediaUiEpoch((e) => e + 1);
                ctx.detachRingTerminatedListener(session);
                ctx.setIncomingRing(null);
                ctx.setSipError(null);
                return;
            }
            ctx.detachRingTerminatedListener(session);
            ctx.conversationSessionRef.current = session;
            ctx.setIncomingRing(null);
            ctx.setSipError(null);
            playInboundCallGreeting(session);
        },
        onCallHangup: (session) => {
            cleanupCallGreeting(session);
            let voiceSessionEnded = false;
            // Remote cancel before answer: clear ringing UI (detaching the listener alone left "Ringing" stuck).
            ctx.setIncomingRing((cur) => {
                if (cur?.session === session) {
                    voiceSessionEnded = true;
                    return null;
                }
                return cur;
            });
            ctx.detachRingTerminatedListener(session);
            const smInner = ctx.mgrRef.current;
            const consultLeg = ctx.consultSessionRef.current === session ? session : null;
            const primaryLeg = ctx.conversationSessionRef.current === session ? session : null;
            if (consultLeg !== null && consultLeg === ctx.consultSessionRef.current) {
                ctx.consultSessionRef.current = null;
                if (!ctx.warmSkipRecoverOnConsultHangupRef.current) {
                    const primary = ctx.conversationSessionRef.current;
                    if (primary && smInner && smInner.isHeld(primary)) {
                        void smInner.unhold(primary).catch(() => undefined);
                    }
                    ctx.setWarmTransfer({ phase: 'idle', destinationLabel: null });
                }
                ctx.warmSkipRecoverOnConsultHangupRef.current = false;
                return;
            }
            if (primaryLeg !== null && primaryLeg === ctx.conversationSessionRef.current) {
                const orphanConsult = ctx.consultSessionRef.current;
                if (orphanConsult && smInner) {
                    void smInner.hangup(orphanConsult).catch(() => undefined);
                }
                ctx.unsubscribeConversationPhaseFnRef.current();
                ctx.conversationSessionRef.current = null;
                ctx.setWarmTransfer({ phase: 'idle', destinationLabel: null });
                voiceSessionEnded = true;
            }
            if (voiceSessionEnded) {
                ctx.onVoiceSessionEnded?.();
            }
        },
    };
}
